import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, Plus, Star, TrendingUp, MessageCircle, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  FeedbackItemWithDetails,
  FeedbackCategory,
  FeedbackStatus,
  FeedbackFilters,
} from '@/types/feedback';
import FeedbackCard from './FeedbackCard';
import FeedbackSubmission from './FeedbackSubmission';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';

interface FeedbackBoardProps {
  onFeedbackClick?: (feedback: FeedbackItemWithDetails) => void;
  className?: string;
}

const FeedbackBoard: React.FC<FeedbackBoardProps> = ({
  onFeedbackClick,
  className,
}) => {
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItemWithDetails[]>([]);
  const [categories, setCategories] = useState<FeedbackCategory[]>([]);
  const [statuses, setStatuses] = useState<FeedbackStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
  const [filters, setFilters] = useState<FeedbackFilters>({
    sort_by: 'most_voted',
  });

  const { isAuthenticated } = useAuth();
  const { toast } = useToast();

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load feedback when filters change
  useEffect(() => {
    loadFeedback();
  }, [filters, searchTerm]);

  const loadInitialData = async () => {
    try {
      const [categoriesResponse, statusesResponse] = await Promise.all([
        supabase
          .from('feedback_categories')
          .select('*')
          .eq('is_active', true)
          .order('display_order'),
        supabase
          .from('feedback_statuses')
          .select('*')
          .eq('is_active', true)
          .eq('is_public', true)
          .order('display_order'),
      ]);

      if (categoriesResponse.error) throw categoriesResponse.error;
      if (statusesResponse.error) throw statusesResponse.error;

      setCategories(categoriesResponse.data || []);
      setStatuses(statusesResponse.data || []);
    } catch (error: any) {
      console.error('Failed to load initial data:', error);
      toast({
        title: 'Failed to load data',
        description: 'Please refresh the page to try again',
        variant: 'destructive',
      });
    }
  };

  const loadFeedback = async () => {
    setIsLoading(true);

    try {
      let query = supabase
        .from('feedback_items')
        .select(`
          *,
          category:feedback_categories(*),
          status:feedback_statuses(*),
          author_profile:user_profiles!feedback_items_author_id_fkey(username, full_name, avatar_url)
        `)
        .eq('is_published', true);

      // Apply filters
      if (filters.category_id) {
        query = query.eq('category_id', filters.category_id);
      }

      if (filters.status_id) {
        query = query.eq('status_id', filters.status_id);
      }

      if (filters.show_featured_only) {
        query = query.eq('is_featured', true);
      }

      if (searchTerm.trim()) {
        query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);
      }

      // Apply sorting
      switch (filters.sort_by) {
        case 'newest':
          query = query.order('created_at', { ascending: false });
          break;
        case 'oldest':
          query = query.order('created_at', { ascending: true });
          break;
        case 'most_voted':
          query = query.order('vote_count', { ascending: false });
          break;
        case 'most_commented':
          query = query.order('comment_count', { ascending: false });
          break;
        default:
          query = query.order('is_featured', { ascending: false })
                      .order('vote_count', { ascending: false })
                      .order('created_at', { ascending: false });
      }

      const { data, error } = await query.limit(50);

      if (error) throw error;

      // Get user votes for authenticated users
      let userVotes: { [key: number]: number } = {};
      if (isAuthenticated && data && data.length > 0) {
        const { data: votesData } = await supabase
          .from('feedback_votes')
          .select('feedback_id, value')
          .in('feedback_id', data.map(item => item.id));

        if (votesData) {
          userVotes = votesData.reduce((acc, vote) => {
            acc[vote.feedback_id] = vote.value;
            return acc;
          }, {} as { [key: number]: number });
        }
      }

      // Add user votes to feedback items
      const feedbackWithVotes = (data || []).map(item => ({
        ...item,
        user_vote: userVotes[item.id] || 0,
      }));

      setFeedbackItems(feedbackWithVotes);
    } catch (error: any) {
      console.error('Failed to load feedback:', error);
      toast({
        title: 'Failed to load feedback',
        description: 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = useCallback((key: keyof FeedbackFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ sort_by: 'most_voted' });
    setSearchTerm('');
  }, []);

  const handleFeedbackSubmitted = useCallback((feedbackId: number, slug: string) => {
    setShowSubmissionDialog(false);
    toast({
      title: 'Feedback submitted successfully',
      description: 'Your feedback will be reviewed before being published.',
    });
    // Refresh feedback list
    loadFeedback();
  }, []);

  const getSortIcon = (sortBy: string) => {
    switch (sortBy) {
      case 'most_voted': return <TrendingUp className="h-4 w-4" />;
      case 'most_commented': return <MessageCircle className="h-4 w-4" />;
      case 'newest': case 'oldest': return <Calendar className="h-4 w-4" />;
      default: return <Star className="h-4 w-4" />;
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Community Feedback</h1>
          <p className="text-muted-foreground">
            Share your ideas and help shape the future of Light Hunt
          </p>
        </div>

        <Dialog open={showSubmissionDialog} onOpenChange={setShowSubmissionDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Submit Feedback
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogTitle className="sr-only">Submit Feedback</DialogTitle>
            <DialogDescription className="sr-only">
              Share your ideas and suggestions to help improve Light Hunt
            </DialogDescription>
            <FeedbackSubmission
              onSuccess={handleFeedbackSubmitted}
              onCancel={() => setShowSubmissionDialog(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search feedback..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Category Filter */}
            <Select
              value={filters.category_id?.toString() || 'all'}
              onValueChange={(value) => 
                handleFilterChange('category_id', value === 'all' ? undefined : parseInt(value))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id.toString()}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      {category.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select
              value={filters.sort_by || 'most_voted'}
              onValueChange={(value) => handleFilterChange('sort_by', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="most_voted">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Most Voted
                  </div>
                </SelectItem>
                <SelectItem value="newest">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Newest
                  </div>
                </SelectItem>
                <SelectItem value="most_commented">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Most Discussed
                  </div>
                </SelectItem>
                <SelectItem value="oldest">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Oldest
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Active Filters */}
          <div className="flex flex-wrap gap-2 mt-4">
            {filters.category_id && (
              <Badge variant="secondary" className="gap-1">
                Category: {categories.find(c => c.id === filters.category_id)?.name}
                <button
                  onClick={() => handleFilterChange('category_id', undefined)}
                  className="ml-1 hover:text-destructive"
                >
                  ×
                </button>
              </Badge>
            )}
            {filters.show_featured_only && (
              <Badge variant="secondary" className="gap-1">
                Featured Only
                <button
                  onClick={() => handleFilterChange('show_featured_only', false)}
                  className="ml-1 hover:text-destructive"
                >
                  ×
                </button>
              </Badge>
            )}
            {searchTerm && (
              <Badge variant="secondary" className="gap-1">
                Search: "{searchTerm}"
                <button
                  onClick={() => handleSearch('')}
                  className="ml-1 hover:text-destructive"
                >
                  ×
                </button>
              </Badge>
            )}
            {(filters.category_id || filters.show_featured_only || searchTerm) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-6 px-2 text-xs"
              >
                Clear All
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Feedback List */}
      <div className="space-y-4">
        {isLoading ? (
          // Loading skeletons
          Array.from({ length: 5 }).map((_, index) => (
            <Card key={index}>
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1">
                    <Skeleton className="h-6 w-6 rounded" />
                    <Skeleton className="h-4 w-8" />
                    <Skeleton className="h-6 w-6 rounded" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <Skeleton className="h-5 w-20" />
                      <Skeleton className="h-5 w-16" />
                    </div>
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))
        ) : feedbackItems.length === 0 ? (
          // Empty state
          <Card>
            <CardContent className="text-center py-12">
              <div className="max-w-md mx-auto">
                <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No feedback found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || Object.keys(filters).some(key => filters[key as keyof FeedbackFilters])
                    ? 'Try adjusting your filters or search terms'
                    : 'Be the first to submit feedback and help improve Light Hunt!'
                  }
                </p>
                <Button
                  onClick={() => setShowSubmissionDialog(true)}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Submit Feedback
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          // Feedback items
          feedbackItems.map((feedback) => (
            <FeedbackCard
              key={feedback.id}
              feedback={feedback}
              onClick={onFeedbackClick}
              showVoting={true}
              showComments={true}
            />
          ))
        )}
      </div>

      {/* Load More Button (for future pagination) */}
      {feedbackItems.length >= 50 && (
        <div className="text-center">
          <Button variant="outline" onClick={loadFeedback}>
            Load More
          </Button>
        </div>
      )}
    </div>
  );
};

export default FeedbackBoard;